import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, Input, Space, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { find, includes, isEmpty, map, noop } from "lodash";
import EmptyContent from "components/EmptyContent";
import { Modal, message } from "components";
import useAppAuth0 from "hooks/useAppAuth0";
import { AICC_LANGUAGE_ACTION, AiccAssetType } from "constants/aiccLanguage";
import { AiccLanguageVersion, AiccManagedLanguage } from "types/model/aiccLanguage";
import * as aiccLanguageAPI from "services/aiccLanguage";
import { useLearningAssetsStore } from "../useLearningAssetsStore";
import { useAiccManagedLanguages } from "../hooks/useAiccManagedLanguages";
import { useAiccTtsVoices } from "../LearningAssets/hooks/useAiccTtsVoices";
import AddLanguageModal from "./Dialogs/AddLanguageModal";
import LanguagePanel from "./LanguagePanel";
import LanguageVersionRows from "./VersionRows";
import PublishModal from "./Dialogs/PublishModal";
import VersionHistoryDrawer from "./Dialogs/VersionHistoryDrawer";
import {
  DELETE_LANGUAGE_CONFIRMATION,
  DELETE_LANGUAGE_OK_TEXT,
  DELETE_LANGUAGE_TITLE,
  DUPLICATE_PREVIEW_OK_TEXT,
  DUPLICATE_PREVIEW_OVERWRITE_OK_TEXT,
  DUPLICATE_PREVIEW_OVERWRITE_TITLE,
  DUPLICATE_PREVIEW_TITLE,
  READY_TO_PUBLISH_MESSAGE,
  READY_TO_PUBLISH_TITLE,
  RESTORE_VERSION_OK_TEXT,
  RESTORE_VERSION_TITLE,
  renderDuplicatePreviewContent,
  renderDuplicatePreviewOverwriteContent,
  renderLanguageDeletionMessage,
  renderRestoreVersionMessage,
  ASSET_SCREEN_PATH,
} from "./configs";
import { AiccLanguageVersionRow, hasWorkingCopy, liveVersionNumber, workingCopyVersionNumber } from "./languageStatus";

import styles from "./LanguageManagement.module.scss";

function LanguageManagement() {
  const history = useHistory();
  const { user } = useAppAuth0();
  const { canManageAiccLanguage, canDuplicateAiccLanguageVersion } = user;
  const {
    languages,
    isLoading,
    isRefreshing,
    refreshingLanguageIds,
    mutate,
    refreshLanguage,
  } = useAiccManagedLanguages();
  const { voicesById } = useAiccTtsVoices();
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [publishingLanguageId, setPublishingLanguageId] = useState<number>();
  const [historyLanguageId, setHistoryLanguageId] = useState<number>();

  const handleDelete = useCallback(
    (language: AiccManagedLanguage) => {
      const modal = Modal.confirm({
        title: DELETE_LANGUAGE_TITLE,
        content: (
          <Space direction="vertical" size={12}>
            {renderLanguageDeletionMessage()}
            <Input
              onChange={(event) =>
                modal.update((prevConfig) => ({
                  ...prevConfig,
                  okButtonProps: {
                    ...prevConfig.okButtonProps,
                    disabled: event.target.value !== DELETE_LANGUAGE_CONFIRMATION,
                  },
                }))
              }
            />
          </Space>
        ),
        okText: DELETE_LANGUAGE_OK_TEXT,
        okButtonProps: { disabled: true },
        onOk: () =>
          aiccLanguageAPI.deleteAiccLanguage(language.id).then(() => {
            message.success("Language deleted successfully");
            mutate().catch(noop);
          }),
      });
    },
    [mutate]
  );

  const handleReadyToPublish = useCallback(
    (language: AiccManagedLanguage) => {
      Modal.confirm({
        title: READY_TO_PUBLISH_TITLE,
        content: READY_TO_PUBLISH_MESSAGE,
        okText: "Confirm",
        onOk: () =>
          aiccLanguageAPI.markAiccLanguageReadyToPublish(language.id).then(() => {
            message.success(`${language.name} is staged for publishing.`);
            refreshLanguage(language.id).catch(noop);
          }),
      });
    },
    [refreshLanguage]
  );

  const handleDuplicatePreview = useCallback(
    (language: AiccManagedLanguage) => {
      // Copying over a copy in progress discards it, so that case gets its own warning and has to
      // ask the backend to overwrite — it answers 409 rather than dropping unpublished edits.
      const isOverwrite = hasWorkingCopy(language);

      Modal.confirm({
        title: isOverwrite ? DUPLICATE_PREVIEW_OVERWRITE_TITLE : DUPLICATE_PREVIEW_TITLE,
        content: isOverwrite
          ? renderDuplicatePreviewOverwriteContent(
              liveVersionNumber(language) ?? workingCopyVersionNumber(language),
              workingCopyVersionNumber(language)
            )
          : renderDuplicatePreviewContent(),
        okText: isOverwrite ? DUPLICATE_PREVIEW_OVERWRITE_OK_TEXT : DUPLICATE_PREVIEW_OK_TEXT,
        okButtonProps: isOverwrite ? { danger: true } : undefined,
        onOk: () =>
          aiccLanguageAPI.createAiccPreview(language.id, isOverwrite).then(() => {
            message.success(`Created a preview of ${language.name} for test accounts.`);
            refreshLanguage(language.id).catch(noop);
          }),
      });
    },
    [refreshLanguage]
  );

  const handleManageAsset = useCallback(
    (language: AiccManagedLanguage, row: AiccLanguageVersionRow, assetType: AiccAssetType) => {
      const path = ASSET_SCREEN_PATH[assetType];
      if (!path) {
        return;
      }

      useLearningAssetsStore.getState().setLanguageId(language.id);
      useLearningAssetsStore.getState().setActiveStatus(row.status);
      history.push(path);
    },
    [history]
  );

  const panelHandlers = {
    onAction: {
      ...(canManageAiccLanguage && {
        [AICC_LANGUAGE_ACTION.deleteLanguage]: handleDelete,
        [AICC_LANGUAGE_ACTION.readyToPublish]: handleReadyToPublish,
        [AICC_LANGUAGE_ACTION.publicToAllUser]: ({ id }: AiccManagedLanguage) => setPublishingLanguageId(id),
      }),
      ...(canDuplicateAiccLanguageVersion && {
        [AICC_LANGUAGE_ACTION.duplicatePreview]: handleDuplicatePreview,
      }),
    },
    onViewVersionHistory: ({ id }: AiccManagedLanguage) => setHistoryLanguageId(id),
  };

  const publishingLanguage = find(languages, { id: publishingLanguageId });
  const historyLanguage = find(languages, { id: historyLanguageId });
  const handleRestore = useCallback(
    (version: AiccLanguageVersion) => {
      if (!historyLanguage) {
        return;
      }
      Modal.confirm({
        title: RESTORE_VERSION_TITLE,
        content: renderRestoreVersionMessage(version.version),
        okText: RESTORE_VERSION_OK_TEXT,
        onOk: () =>
          aiccLanguageAPI.restoreAiccVersion(historyLanguage.id, version.version).then(() => {
            message.success("Version successfully restored to preview-ready state.");
            refreshLanguage(historyLanguage.id).catch(noop);
          }),
      });
    },
    [historyLanguage, refreshLanguage]
  );

  return (
    <Card
      title={
        <Space size={12}>
          Language Management
          {isRefreshing && (
            <span className={styles.syncing}>
              <Spin size="small" indicator={<LoadingOutlined spin />} />
              Syncing the latest list
            </span>
          )}
        </Space>
      }
      className="page-container"
      extra={
        canManageAiccLanguage && (
          <Button type="primary" shape="round" onClick={() => setIsAddModalVisible(true)}>
            + Add Language
          </Button>
        )
      }
    >
      {isLoading ? (
        <div className={styles.listLoading}>
          <Spin />
        </div>
      ) : (
        <>
          {isEmpty(languages) ? (
            <div className={styles.emptyState}>
              <EmptyContent displayTexts={["No language yet.", "Please add at least one language"]} />
            </div>
          ) : (
            map(languages, (language) => (
              <LanguagePanel
                key={language.id}
                language={language}
                isRefreshing={includes(refreshingLanguageIds, language.id)}
                {...panelHandlers}
              >
                {/*
                  A component rather than the rows themselves, so the versions are fetched only once
                  this panel is opened: antd mounts a panel's children on first expand, and the list
                  is read without them for exactly that reason.
                */}
                <LanguageVersionRows
                  language={language}
                  onAction={panelHandlers.onAction}
                  onManage={handleManageAsset}
                  voicesById={voicesById}
                  isRefreshing={includes(refreshingLanguageIds, language.id)}
                />
              </LanguagePanel>
            ))
          )}
        </>
      )}

      <AddLanguageModal visible={isAddModalVisible} onCancel={() => setIsAddModalVisible(false)} onAdded={mutate} />

      <PublishModal
        language={publishingLanguage}
        onCancel={() => setPublishingLanguageId(undefined)}
        onPublished={refreshLanguage}
      />

      <VersionHistoryDrawer
        language={historyLanguage}
        onClose={() => setHistoryLanguageId(undefined)}
        onRestore={canManageAiccLanguage ? handleRestore : undefined}
      />
    </Card>
  );
}

export default LanguageManagement;
